<template>
  <span class="el-help-wrap">
    <b-button
      size="sm"
      variant="outline-secondary"
      title="How to use the Event Log"
      aria-label="Help"
      @click="show_help = true"
    >
      <i class="fa fa-info-circle"></i>
    </b-button>

    <b-modal
      v-model="show_help"
      title="Using the Event Log"
      size="lg"
      ok-only
      scrollable
    >
      <div class="el-help-body">
        <h4>What this page is</h4>
        <p>
          The Event Log is the running record of everything your team has filed in Event
          Response Manager: issues, kudos, and notes, newest first. This is the page for
          searching and reading the record. The <b>Event Board</b> shows the same events as
          lanes for working the queue, and the <b>ERM Dashboard</b> counts and charts them.
        </p>

        <hr/>

        <h4>The three kinds of entry</h4>
        <ul>
          <li>
            <b>Issue</b>: something that went wrong or needs attention. A breakdown, an
            accident, a delay, a complaint, a near miss, an injury, lost and found.
          </li>
          <li><b>Kudos</b>: recognition for a driver or team member who did something well.</li>
          <li>
            <b>Note</b>: a neutral record that isn't an incident, such as an FYI, a booking
            change, or general context.
          </li>
        </ul>
        <p>
          Every entry also carries a severity. It starts from the category and can be changed
          when the entry is filed or later on the event. Normal is the quiet default, so only
          <b>High</b> and <b>Low</b> show a chip in the list, and a High row is tinted so it
          stands out while it is open.
        </p>

        <hr/>

        <h4>Narrowing the list</h4>
        <ul>
          <li>
            The <b>Status</b> and <b>Type</b> chips carry a live count, such as
            <b>New (3)</b>, so you can see what is waiting before you click. Severity chips
            don't. <b>All</b> clears a group.
          </li>
          <li>
            Under <b>Include</b>, the <b>archived</b> chip brings back events whose category
            a fleet admin has turned off. Without it those events stay out of the log,
            analytics and exports, and the board and the huddle leave them out either way.
            The chip widens the list rather than narrowing it, so <b>Clear filters</b>
            turns it off too, and a category marked <b>(archived)</b> in the Category list
            turns it on for you.
          </li>
          <li>
            The date presets (<b>Today</b>, <b>7d</b>, <b>30d</b>, <b>90d</b>) filter by when
            the event was filed. <b>Custom</b> reveals From and To pickers for any range.
          </li>
          <li>
            <b>Category</b>, <b>Assigned</b>, <b>Driver</b>, and <b>Vehicle</b> sit on the
            row. Each list offers every value in your whole history, not just the events
            on screen. <b>Assigned</b> lists <b>Mine</b> right under <b>Anyone</b>, so your own
            queue is one pick away.
          </li>
          <li>
            The <b>search</b> box matches the description, driver, vehicle, charter,
            customer, location, action items, follow-up notes, category, and any extra
            fields on the entry. Press Enter to search. Type a bare <b>#123</b> to jump
            straight to that event. A number finds whole charter numbers, so <b>15691</b>
            also finds 15691-0 but not 156910.
          </li>
          <li>
            Every control stacks, and the filters apply as you change them. The list loads in
            pages as you scroll, so keep scrolling to reach older events. Click the
            <b>#</b> or <b>Created</b> header to sort the other way.
          </li>
          <li>
            The list keeps itself current while you have it open. Rows you can see update
            in place. Events filed while you are scrolled down arrive as a
            <b>N new events</b> button at the top of the page; tap it to bring them in.
            On a phone, the list catches up when you come back to the tab.
          </li>
          <li>
            On a phone the table becomes a card list and the chips, dates, and pickers fold
            behind a <b>Filters</b> button. Its badge counts what is still narrowing the
            list. Search stays out in the open.
          </li>
        </ul>

        <hr/>

        <h4>Adding an entry</h4>
        <ul>
          <li>
            <b>Add entry</b> opens the form. Pick <b>Issue</b>, <b>Kudos</b>, or
            <b>Note</b>. For an issue or kudos, choose a category; a note needs none.
            Every issue also asks, optionally, what the vehicle was doing at the time.
            Some categories ask a few extra questions, and an issue's severity is pre-set
            from its category so you only touch it when the default is wrong.
          </li>
          <li>
            Describe what happened, and add the driver, vehicle, charter, customer, and
            location when you know them. <b>Customer</b> is who the trip was for: a group, a
            company, or a person. The driver and vehicle fields suggest names from your roster
            as you type, which links the event to that driver or unit. A name that isn't on the
            roster is still accepted, and can be linked later on the event.
          </li>
          <li>
            A kudos asks who it is for once. Pick a driver and the name field suggests from
            your roster; pick a staff member and type their name.
          </li>
          <li>
            If the matter has already been handled, put that under <b>Follow-up &amp;
            resolution</b> now rather than reopening the event to add it.
          </li>
          <li>
            Attach photos if you have them, up to 10 per event (JPEG, PNG, GIF, or WebP).
          </li>
          <li>
            Saving closes the form, and a notice shows the new event's number. Click the
            notice to open the event. The notice closes on its own after a few seconds, and
            the new event is highlighted in the list.
          </li>
          <li>
            Your default recipients are notified when an entry is filed. People
            outside the portal can file through your public form; a fleet admin sets that up
            in Event Response Manager settings (below).
          </li>
        </ul>

        <hr/>

        <h4>Export and settings</h4>
        <ul>
          <li>
            <b>Export CSV</b> downloads the events currently in view, with your filters
            applied, up to 10,000 rows. Each row also carries the event's comments and its
            activity log as two columns, one entry per line with a timestamp and author, so
            the resolution narrative travels with the event. The coaching columns
            (preventability and coaching note) are included only for Tech Support and fleet
            admins.
          </li>
          <li>
            Fleet admins find <b>Settings</b> under <b>Event Response Manager</b> in the
            left navigation (in Classic, the gear at the top right, then the Event Response
            Manager tab). It holds general options such as the duplicate check and email
            rules, the categories with their recipients, departments, the public form and
            its link, and the Huddle schedule.
          </li>
        </ul>

        <hr/>

        <h4>Where to go next</h4>
        <p>
          Click any row to open the event, where you can change its status, assign it, edit
          the details, add photos, comment, and print. The <b>Event Board</b> is the same
          events as four lanes by status, for working the queue. The <b>ERM Dashboard</b>
          charts the trend and ranks events by category, driver, and vehicle.
        </p>
      </div>
    </b-modal>
  </span>
</template>

<script>
// how-to modal for the ERM Event Log, following the erm_board_help /
// driver_manager_*_help house pattern (info-icon button + ok-only scrollable
// modal). the log carries no standing description, so this is where the
// three entry types, the filter row, and the add-entry flow get explained.
export default {
  name: 'erm_log_help',
  data() {
    return { show_help: false }
  },
}
</script>

<style scoped>
.el-help-body h4 { font-size: 1.05rem; margin-top: 4px; }
.el-help-body p, .el-help-body li { font-size: 0.95rem; }
.el-help-body li { margin-bottom: 6px; }
</style>
